import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
} from 'recharts';
import { motion } from 'framer-motion';
import styles from './DashboardChart.module.css';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { useFormik } from 'formik';
import Icon from '../../../../components/Icon/Icon';
import { useEffect, useState } from 'react';
import {
	getCurrentMonthFormatted,
	// getCurrentYearFormatted,
	getGraphTimeFormat,
	getReverseGraphTime,
} from '../../../../utils/getDate.utils';
import {
	generateGraphData,
	generatePFMGraphData,
} from '../../../../utils/common.utils';
import {
	getGraphFilterMapping,
	horizontalAxisMapping,
} from '../../../../utils/graphMapping.utils';
import { useSelector } from 'react-redux';

const currentTime = new Date().toISOString().slice(0, 19);
// const currentYear = getCurrentYearFormatted();
const currentMonth = getCurrentMonthFormatted();

const DashboardChart = (props) => {
	const { filterOptions } = props;
	const isPFM = useSelector((state) => state.auth.isPFM);
	const initialFilters = {
		filter: filterOptions[0],
		startDate: getReverseGraphTime(currentMonth),
		endDate: getReverseGraphTime(currentTime),
		timeRange: 'DAILY',
	};
	const { values, handleChange, handleBlur } = useFormik({
		initialValues: initialFilters,
	});
	const [graphData, setGraphData] = useState(null);
	const [inputData, setInputData] = useState();

	//Setting input depending on filters
	useEffect(() => {
		setInputData(() => {
			const metricsName = getGraphFilterMapping(
				values?.filter,
				values?.timeRange
			);
			const timeIntervalEnum = values?.timeRange;
			const startTime = getGraphTimeFormat(values?.startDate);

			// Check if the selected endDate is the current month or year
			let endDate = getGraphTimeFormat(values?.endDate);
			const today = new Date();
			const selectedDate = new Date(values?.endDate);
			if (timeIntervalEnum === 'MONTHLY') {
				if (
					selectedDate.getMonth() === today.getMonth() &&
					selectedDate.getFullYear() === today.getFullYear()
				) {
					endDate = getGraphTimeFormat(today); // Set endDate to current date
				}
			} else if (timeIntervalEnum === 'YEARLY') {
				if (selectedDate.getFullYear() === today.getFullYear()) {
					endDate = getGraphTimeFormat(today); // Set endDate to current date
				}
			}

			return [
				{
					metricsName,
					timeIntervalEnum,
					startTime,
					endTime: endDate,
				},
			];
		});
	}, [values]);

	// console.log('inputData: ', inputData);

	//Make API call to get graphData
	useEffect(() => {
		isPFM
			? generatePFMGraphData(inputData, setGraphData)
			: generateGraphData(inputData, setGraphData);
	}, [inputData, isPFM]);

	// console.log(graphData);

	//Make chartData from graphData
	const chartData = graphData
		? graphData[0].dataPoints.map((pair, index) => {
				const name = horizontalAxisMapping(values.timeRange, pair);
				return {
					name: name,
					value: parseInt(pair.value, 10),
				};
		  })
		: {};

	const calendarComponent = (isStart = false) => {
		if (values.timeRange === 'DAILY')
			return (
				<div>
					<p className={styles['filter-label']}>{`${
						isStart ? 'Start' : 'End'
					} date:`}</p>
					<div className={`p-inputgroup ${styles.filter}`}>
						<Icon icon="calendar_month" className="p-inputgroup-addon" />
						<Calendar
							id={isStart ? 'startDate' : 'endDate'}
							name={isStart ? 'startDate' : 'endDate'}
							placeholder={`Enter ${isStart ? 'start' : 'end'} date`}
							mask="99/99/9999"
							value={isStart ? values.startDate : values.endDate}
							onChange={handleChange}
							onBlur={handleBlur}
							dateFormat="dd/mm/yy"
						/>
					</div>
				</div>
			);
		else if (values.timeRange === 'MONTHLY') {
			return (
				<div>
					<p className={styles['filter-label']}>{`${
						isStart ? 'Start' : 'End'
					} month:`}</p>
					<div className={`p-inputgroup ${styles.filter}`}>
						<Icon icon="calendar_month" className="p-inputgroup-addon" />
						<Calendar
							id={isStart ? 'startDate' : 'endDate'}
							name={isStart ? 'startDate' : 'endDate'}
							placeholder={`Enter ${isStart ? 'start' : 'end'} month`}
							mask="99/9999"
							view="month"
							value={isStart ? values.startDate : values.endDate}
							onChange={handleChange}
							onBlur={handleBlur}
							dateFormat="mm/yy"
						/>
					</div>
				</div>
			);
		} else
			return (
				<div>
					<p className={styles['filter-label']}>{`${
						isStart ? 'Start' : 'End'
					} year:`}</p>
					<div className={`p-inputgroup ${styles.filter}`}>
						<Icon icon="calendar_month" className="p-inputgroup-addon" />
						<Calendar
							id={isStart ? 'startDate' : 'endDate'}
							name={isStart ? 'startDate' : 'endDate'}
							placeholder={`Enter ${isStart ? 'start' : 'end'} year`}
							mask="9999"
							view="year"
							value={isStart ? values.startDate : values.endDate}
							onChange={handleChange}
							onBlur={handleBlur}
							dateFormat="yy"
						/>
					</div>
				</div>
			);
	};

	return (
		<div className={styles['chart__container']}>
			{/* Filters */}
			<div className={styles['filters__container']}>
				<h4 className={styles.heading}>{values.filter}</h4>
				<div>
					<p className={styles['filter-label']}>Metric: </p>
					<Dropdown
						className={styles.filter}
						inputId="filter"
						name="filter"
						placeholder="Select a metric"
						value={values.filter}
						onChange={handleChange}
						onBlur={handleBlur}
						options={filterOptions}
					/>
				</div>
				<div>
					<p className={styles['filter-label']}>Time Interval: </p>
					<Dropdown
						className={styles.filter}
						inputId="timeRange"
						name="timeRange"
						placeholder="Select a time interval"
						value={values.timeRange}
						onChange={handleChange}
						onBlur={handleBlur}
						options={['DAILY', 'MONTHLY', 'YEARLY']}
					/>
				</div>
				{calendarComponent(true)}
				{calendarComponent()}
			</div>
			<hr />
			{/* Chart */}
			<motion.div
				initial={{ opacity: 0, scale: 0.5 }}
				animate={{ opacity: 1, scale: 1 }}
				transition={{ duration: 0.5 }}
			>
				<ResponsiveContainer width="100%" height={300}>
					<LineChart data={chartData} className={styles['line-chart']}>
						<Line
							type="monotone"
							dataKey="value"
							stroke="#8884d8"
							dot={{ r: 5 }}
							activeDot={{ r: 6 }}
						/>
						<XAxis dataKey="name" />
						<YAxis />
						<Tooltip />
					</LineChart>
				</ResponsiveContainer>
			</motion.div>
		</div>
	);
};

export default DashboardChart;
